/* eslint-disable sonarjs/cognitive-complexity */
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React, { useState, useContext, useEffect, useRef } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useSelector } from 'react-redux';
import {
  BESTSELLER,
  PROCEED,
  PLEASE_CHOOSE,
  GIVE_AS_A_GIFT,
  PLEASE_CHOOSE_ONE_OPTION,
  CHOOSE_YOUR_OPTIONS,
  DEPOSIT_MESSAGE,
  DEPOSIT_PAY_DEPOSIT,
  DEPOSIT_PAY_FULL,
  RESERVE_CHECKOUT,
  PRODUCT_FEATURES,
  CUE_VIP_EXCLUSIVE,
  BESTSELLER_EXPLAIN,
  CHOOSE_YOUR_OPTIONS_TEXT,
  NIBBLE_TIMER,
} from '../../../../config/text/text';
import { trackEvent } from '../../../../helpers/analytics';
import { isLightPage } from '../../../../helpers/browser';
import useCheckMobile from '../../../../helpers/checkMobile';
import {
  getColumn,
  getImageForVariant,
  getRow,
  getDiscount,
} from '../../../../helpers/chooseYourOptions';
import { formatCurrency } from '../../../../helpers/currency';
import {
  getModalSocialCue,
  useSocialCues,
  isDepositDeal,
} from '../../../../helpers/deals';
import { replaceImageServer } from '../../../../helpers/image';
import { getBackendSocialCueMessage } from '../../../../helpers/socialCues';
import ThemeContext from '../../../../providers/ThemeProvider';
import Message from '../../../_generic/Message';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
import RadioButtonWithLabel from '../../../_generic/radio-button/RadioButtonWithLabel';
import VipTextOverlay from '../../../_generic/vip-text-overlay/VipTextOverlay';
import {
  isNibbleEnabled,
  nibbleDwellTimer,
} from '../../../nibble/NibbleHelper';
import NibbleWidget from '../../../nibble/NibbleWidget';
import MainDealDiscount from '../../MainDealDiscount';
import MainDealFinePrint from '../../MainDealFinePrint';
import MainDealFullDetails from '../../MainDealFullDetails';
import MainDealList from '../../mainDealList';
import MainDealSocialCue from '../../mainDealSocialCue';
import DealBuyButton from './DealBuyButton';
import DealGiftButton from './DealGiftButton';
import ExpressCheckoutText from './ExpressCheckoutText';
/**
 * see https://wowcher.atlassian.net/wiki/spaces/WOWA/pages/2266005505/Choose+Your+Options
 */

export const layoutOptions = {
  travel: 'travel',
};

const ChooseYourQuantity = dynamic(() => import('../ChooseYourQuantity'), {
  ssr: false,
});

const DealChooseYourOptionsModal = ({
  buttonRef = undefined,
  deal = {},
  finePrintCollapsed,
  handleBuyButtonClick,
  initialColumnSel = 0,
  isOptionSelected = () => {},
  isVisible = false,
  isModernVipVariant = false,
  layout,
  secondCheckout = false,
  setFinePrintCollapsed,
  isAuthenticated = false,
  isTypeMobile = false,
  buyButton = null,
  isExpressDealPage = false,
  selectedOptions,
  paymentData = null,
  setIsCYOSelected,
}) => {
  const [isVipUser] = useSelector((state) => [
    state?.user?.userprofile?.vipUser,
  ]);
  const isMobile = useCheckMobile(false);
  const [rowSelection, setRowSelection] = useState(0); // 1st option
  const [columnSelection, setColumnSelection] = useState(initialColumnSel); // 2nd Option (hidden if only 1 option)
  const [selectedOption, setSelectedOption] = useState(-1);
  const [payDeposit, setPayDeposit] = useState(deal?.depositAvailable || false); // deposit or payment in full
  const [showEmptyMessage, setShowEmptyMessage] = useState(false);
  const theme = useContext(ThemeContext);
  const socialCuesData = useSocialCues(deal.id);
  const row = getRow(deal, isVipUser);
  const [column, setColumn] = useState(() => getColumn(deal, rowSelection));
  const bestsellerProduct = deal?.products?.find(
    (element) => element.bestSellerClaim,
  );
  const [showNibble, setShowNibble] = useState(false);
  const hasTracked = useRef(false);
  const didTrackNibble = useRef(false);
  const didTrackNibblePromtClick = useRef(false);

  useEffect(() => {
    if (selectedOption < 0) return setShowNibble(false);

    const product = deal?.products[selectedOption];
    const productId = product?.id;
    setIsCYOSelected?.(true);

    const handleTracking = (type, trackRef) => {
      if (!trackRef.current) {
        trackEvent(`nibble_button_${type}`);
        trackRef.current = true;
      }
    };

    if (localStorage.getItem(NIBBLE_TIMER) === 'true') {
      const [wouldAppear, appears] = [true, false].map((flag) =>
        isNibbleEnabled(deal, productId, isVipUser, flag),
      );

      wouldAppear && trackEvent('nibble_button_would_appear_from_prompt_click');
      appears && trackEvent('nibble_button_appear_from_prompt_click');
      didTrackNibblePromtClick.current || wouldAppear || appears;
      if (wouldAppear) didTrackNibble.current = true;
      if (appears) hasTracked.current = true;
    }

    nibbleDwellTimer().then(() => {
      const [wouldLoad, shouldAppear] = [true, false].map((flag) =>
        isNibbleEnabled(deal, productId, isVipUser, flag),
      );

      wouldLoad && handleTracking('would_have_loaded', didTrackNibble);
      if (shouldAppear) {
        handleTracking('appear', hasTracked);
        setShowNibble(true);
      }
    });
  }, [selectedOption]);

  useEffect(() => {
    return () => {
      hasTracked.current = false; // Reset tracking flag on component unmount
    };
  }, []);

  const isLayoutTravel = Boolean(layout === layoutOptions.travel);

  const onRowChange = (rowId) => {
    const id = Number.parseInt(rowId, 10);
    const col = getColumn(deal, id, isVipUser);
    setRowSelection(id);
    setColumn(col);
    const colSelection = col.items.length === 1 ? col.items[0].id : 0;

    setColumnSelection(colSelection);

    const selection = deal.products.findIndex(
      (item) => item.rowId === id && item.columnId === colSelection,
    );
    setSelectedOption(selection);
    setShowEmptyMessage(false);
    if (column.items.length === 1) isOptionSelected(true);
  };

  const onSecondRowChange = (colId) => {
    const secondValue = Number.parseInt(colId, 10);
    setColumnSelection(secondValue);

    if (deal.depositAvailable) {
      setPayDeposit(Boolean(secondValue));
    }
    const selection = deal.products.findIndex(
      (item) => item.rowId === rowSelection && item.columnId === secondValue,
    );
    setSelectedOption(selection);
    setShowEmptyMessage(false);
    isOptionSelected(true);
  };

  const showError = () => {
    setShowEmptyMessage(true);
  };

  const DepositMessage = () => {
    if (!isDepositDeal(deal)) {
      return null;
    }

    return (
      <Message
        extraClasses="message-center message-border-radius green-theme"
        html
        message={DEPOSIT_MESSAGE.replace(
          '%price',
          formatCurrency(deal.currency, deal?.depositPrice),
        )}
        showIcon={false}
      />
    );
  };

  const getGiftButton = (itemSelected) => {
    if (!deal.giftable) return null;
    if (itemSelected < 0)
      return (
        <div className="gift-button">
          <PrimaryButton
            aria-label={GIVE_AS_A_GIFT}
            className="full-width outline margin"
            onClick={() => showError()}
          >
            {GIVE_AS_A_GIFT}
          </PrimaryButton>
        </div>
      );

    return (
      <div className="dealgift-button">
        <DealGiftButton
          className={`${
            isExpressDealPage
              ? `full-width common-express-style cyo-sec ${
                  showNibble ? 'top-margin' : ''
                } `
              : 'full-width outline margin'
          }`}
          deal={deal}
          position={selectedOption}
        />
      </div>
    );
  };
  const getDropdownDisplayItem = (item) => {
    if (item?.isModernVipVariant && !isVipUser) {
      return (
        <span className={`text-wrap ${item.isBestseller ? 'green-text' : ''}`}>
          <span>{`${item?.modernSplatVariantData?.header} - ${item?.modernSplatVariantData?.newFormatPrefix}`}</span>
          <span className="vip-new-variant">
            {item?.modernSplatVariantData?.vipPrice}
          </span>
          &nbsp;
          <span>
            <span className="vip-variant-nonvip">
              {`Non VIP ${item?.modernSplatVariantData?.normalPrice} `}
            </span>
            {` ${item?.isBestseller ? `- ${BESTSELLER}` : ''}`}
          </span>
        </span>
      );
    } else if (item?.isBestseller && !item?.disable) {
      return (
        <span className="green-text text-wrap">
          {item?.displayAs} - {BESTSELLER}
        </span>
      );
    } else {
      return <span className="text-wrap">{item?.displayAs}</span>;
    }
  };

  const renderComplianceText =
    isExpressDealPage &&
    buyButton === CHOOSE_YOUR_OPTIONS_TEXT &&
    selectedOption >= 0;

  const imageVariant = getImageForVariant(deal, rowSelection, columnSelection);
  const selectBox = (data, value, action, htmlId) => {
    data.items.sort((a, b) => a.id - b.id);
    const selectedItem = value ? data.items.find((x) => x.id === value) : null;

    return (
      <>
        <Dropdown>
          <Dropdown.Toggle className="dropdown-outline" id={htmlId}>
            {selectedItem
              ? getDropdownDisplayItem(selectedItem)
              : `${PLEASE_CHOOSE} ${data.label}`}
          </Dropdown.Toggle>

          <Dropdown.Menu className="full-width-list" id="menu-list">
            <Dropdown.Item
              className="full-width-list"
              onClick={() => action(0)}
            >
              {PLEASE_CHOOSE} {data.label}
            </Dropdown.Item>

            {data.items.map((item) => {
              if ((isLightPage() && !item.disabled) || !isLightPage())
                return (
                  <Dropdown.Item
                    className="dropdown-item"
                    disabled={item.disabled}
                    key={item.id}
                    onClick={() => action(item.id)}
                    value={item.id}
                  >
                    {getDropdownDisplayItem(item)}
                    {item.vipExclusive && (
                      <span className="green-text text-wrap">
                        {CUE_VIP_EXCLUSIVE}
                      </span>
                    )}
                  </Dropdown.Item>
                );

              return null;
            })}
          </Dropdown.Menu>
        </Dropdown>

        <style jsx>
          {`
            :global(.dropdown-outline) {
              width: 100%;
              background-color: transparent;
              color: ${theme.commoncolors.greycharcoal};
              border: 1px solid ${theme.commoncolors.inputiconcolor};
              font-size: 14px;
              min-width: 50px;
              min-height: 28px;
              border-radius: 5px;
              padding: 0px 10px 0px 8px;
              appearance: none;
              line-height: 1.2em;
              margin-bottom: 10px;
              text-align: left;
            }
            :global(.dropdown-outline:hover) {
              background-color: transparent;
              color: ${theme.commoncolors.greycharcoal};
              border: 1px solid ${theme.commoncolors.inputiconcolor};
            }
            :global(.dropdown-outline.dropdown-toggle) {
              background-color: transparent !important;
              color: ${theme.commoncolors.greycharcoal} !important;
              border: 1px solid ${theme.commoncolors.inputiconcolor} !important;
            }
            :global(.dropdown-outline::after) {
              float: right;
              margin-top: 6px;
            }
            :global(.full-width-list) {
              width: 100%;
              font-size: 13px;
            }
            :global(.dropdown-item) {
              display: flex;
              align-items: center;
              gap: 1rem;
            }
            :global(.green-text) {
              color: ${theme.commoncolors.assure};
            }
            .text-wrap {
              white-space: normal;
            }

            @media (max-width: ${theme.breakpoints.smUp}) {
              .outline.green-text {
                display: none;
              }
            }
          `}
        </style>
      </>
    );
  };

  const getProceedButtonText =
    payDeposit && isDepositDeal(deal) ? RESERVE_CHECKOUT : PROCEED;

  const ProceedButton = () => {
    return (
      <>
        {selectedOption < 0 && (
          <PrimaryButton
            aria-label={getProceedButtonText}
            className="size-larger full-width margin"
            onClick={showError}
          >
            {getProceedButtonText}
          </PrimaryButton>
        )}
      </>
    );
  };

  const ChooseYourOptionsInputs = () => {
    return (
      <div className="choose-your-options__inputs">
        {/* ROW SELECTION */}
        {selectBox(row, rowSelection, onRowChange, 'rowId')}

        {/* COLUMN SELECTION (HIDDEN IF ONLY 1 OPTION) */}
        {column.items.length > 1 &&
          selectBox(column, columnSelection, onSecondRowChange, 'columnId')}
        {column.items.length > 1
          ? (bestsellerProduct?.columnId === columnSelection ||
              bestsellerProduct?.rowId === rowSelection) && (
              <div className="deal-product-selector__footnote">
                {BESTSELLER_EXPLAIN}
              </div>
            )
          : bestsellerProduct?.rowId === rowSelection && (
              <div className="deal-product-selector__footnote">
                {BESTSELLER_EXPLAIN}
              </div>
            )}
        <div
          className={`deal-product-selector__empty-message ${
            showEmptyMessage ? 'visible ' : 'hidden'
          }`}
        >
          {PLEASE_CHOOSE_ONE_OPTION}
        </div>
      </div>
    );
  };

  const PayAmount = () => {
    if (!isDepositDeal(deal)) return false;

    return columnSelection ? (
      <div className="cyo-deposit">
        <RadioButtonWithLabel
          checked={payDeposit}
          className={classNames('radio-button--boxed radio-button--switch', {
            'radio-button--green': payDeposit,
          })}
          label={
            <>
              <p className="label-text">{DEPOSIT_PAY_DEPOSIT}</p>
              <p className="label-price">
                {formatCurrency(deal.currency, deal?.depositPrice)}
              </p>
            </>
          }
          onChange={() => setPayDeposit(true)}
        />
        <RadioButtonWithLabel
          checked={!payDeposit}
          className={classNames('radio-button--boxed radio-button--switch', {
            'radio-button--green': !payDeposit,
          })}
          label={
            <>
              <p className="label-text">{DEPOSIT_PAY_FULL}</p>
              <p className="label-price">
                {formatCurrency(deal.currency, deal?.price)}
              </p>
            </>
          }
          onChange={() => setPayDeposit(false)}
        />
      </div>
    ) : null;
  };

  const SocialCues = () => {
    return (
      <div className="choose-your-options__cue">
        <div className="choose-your-options__discount">
          <MainDealDiscount
            deal={getDiscount(deal, rowSelection, columnSelection)}
          />
        </div>
        <MainDealSocialCue
          className="main-deal-social-cue--assure main-deal-social-cue--no-margin"
          cue={
            deal.display?.bought && deal.totalBought === 0
              ? deal.newDealMessageTag
              : null
          }
        />
        <MainDealSocialCue
          className="main-deal-social-cue--alert"
          cue={getModalSocialCue({
            deal,
            selected: selectedOption,
          })}
          isStarMarked
        />
        <MainDealSocialCue
          className="main-deal-social-cue--assure"
          cue={getBackendSocialCueMessage(socialCuesData)}
        />
        <VipTextOverlay content={deal?.vipDealOverlayText} rounded />
      </div>
    );
  };

  return (
    <>
      {isLayoutTravel && (
        <div className="cyo-block">
          <div className="cyo-header">{CHOOSE_YOUR_OPTIONS}</div>
          <div className="cyo-body">
            <DepositMessage />
            <div className="cyo-options">{ChooseYourOptionsInputs()}</div>
            <PayAmount />
            <SocialCues />
            <div className="cyo-buttons">
              <ProceedButton />
              {selectedOption >= 0 && (
                <DealBuyButton
                  buttonRef={buttonRef}
                  buyButton={buyButton}
                  className={`${
                    isExpressDealPage
                      ? 'deal-main-deal__buy full-width cyo-sec common-express-style'
                      : 'size-larger deal-main-deal__buy full-width margin'
                  }`}
                  deal={deal}
                  handleBuyButtonClick={handleBuyButtonClick}
                  isExpressDealPage={isExpressDealPage}
                  payDeposit={payDeposit}
                  paymentInstruments={paymentData}
                  position={selectedOption}
                  text={getProceedButtonText}
                  trackingEnabled={false}
                />
              )}
              {selectedOption >= 0 && showNibble && (
                <NibbleWidget
                  cyo
                  deal={deal}
                  variantId={deal?.products[selectedOption].id}
                />
              )}
              {getGiftButton(selectedOption)}
              {/* {isMobile && renderComplianceText && (
                <ExpressCheckoutText
                  isAuthenticated={isAuthenticated}
                  margin="0px 0px 0px 8px"
                  shouldRenderComplianceText={renderComplianceText}
                />
              )} */}
            </div>
          </div>
        </div>
      )}

      {!isLayoutTravel && (
        <>
          <h2>{deal.headline}</h2>
          {!isDepositDeal(deal) && (
            <div className="choose-your-options__image-container">
              {imageVariant ? (
                <img
                  alt={deal.headline}
                  className="choose-your-options__image"
                  src={replaceImageServer({
                    dealId: deal.id,
                    image: imageVariant,
                  })}
                />
              ) : (
                <img
                  alt={deal.headline}
                  className="choose-your-options__image"
                  src={replaceImageServer({
                    dealId: deal.id,
                    image: deal?.images[0],
                  })}
                />
              )}
            </div>
          )}
          {secondCheckout && (
            <>
              <MainDealList
                className="highlights"
                list={deal.highlights}
                title={PRODUCT_FEATURES}
              />
              <MainDealFullDetails deal={deal} />
              <MainDealFinePrint
                collapsed={finePrintCollapsed}
                deal={deal}
                onToggle={() => setFinePrintCollapsed((state) => !state)}
              />
            </>
          )}
          <DepositMessage />
          {ChooseYourOptionsInputs()}
          {isVisible && <ChooseYourQuantity />}
          <SocialCues />
          <PayAmount />
          <ProceedButton />
          {selectedOption >= 0 && (
            <DealBuyButton
              buyButton={buyButton}
              className={`${
                isExpressDealPage
                  ? 'deal-main-deal__buy full-width cyo-sec common-express-style'
                  : 'size-larger deal-main-deal__buy full-width margin'
              }`}
              deal={deal}
              handleBuyButtonClick={handleBuyButtonClick}
              isExpressDealPage={isExpressDealPage}
              isTypeMobile
              payDeposit={payDeposit}
              paymentInstruments={paymentData}
              position={selectedOption}
              secondCheckout={secondCheckout}
              selectedOptions={selectedOptions}
              text={getProceedButtonText}
              trackingEnabled={false}
            />
          )}
          {selectedOption >= 0 && showNibble && (
            <NibbleWidget
              cyo
              deal={deal}
              variantId={deal?.products[selectedOption].id}
            />
          )}
          {getGiftButton(selectedOption)}

          {isMobile && renderComplianceText && (
            <ExpressCheckoutText
              isAuthenticated={isAuthenticated}
              margin="0px 0px 0px 8px"
              shouldRenderComplianceText={renderComplianceText}
            />
          )}
        </>
      )}

      <style jsx>{`
        .cyo-header {
          background-color: ${theme.colors.primary};
          min-height: 50px;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          text-align: center;
          font-size: 20px;
          color: ${theme.colors.textonprimary};
          text-transform: capitalize;
        }

        .cyo-body {
          display: flex;
          flex-direction: column;
          border: 4px solid ${theme.colors.primary};
          border-top: none;
          padding: 20px;
        }
        .cyo-buttons {
          padding-top: 10px;
        }
        .cyo-block {
          margin-bottom: 20px;
        }
        :global(.cyo-deposit) {
          display: flex;
          padding: 5px 0px 10px 0px;
        }
        :global(.vip-new-variant) {
          background-color: ${theme.colors.splatvariant.vip}; /* pink */
          color: ${theme.colors.splatvariant.textvip};
          border-radius: 12px;
          padding: 2px 6px;
          line-height: 1;
          vertical-align: middle;
          display: inline-block;
        }
        :global(.vip-variant-nonvip) {
          background-color: ${theme.commoncolors.greyxlighter};
          color: ${theme.commoncolors.greycharcoal};
          border-radius: 12px;
          padding: 2px 6px;
          line-height: 1;
          vertical-align: middle;
          display: inline-block;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.cyo-deposit) {
            padding-bottom: 10px;
          }
        }
        :global(.cyo-deposit label) {
          flex-grow: 1;
        }
        :global(.cyo-deposit label .label-text),
        :global(.cyo-deposit label .label-price) {
          margin: 0px;
        }
        :global(.cyo-deposit label:first-child) {
          margin-right: 10px;
        }

        :global(.choose-your-options__cue) {
          overflow: hidden;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.choose-your-options__cue) {
            padding-top: 10px;
          }
        }
        :global(.choose-your-options__discount) {
          float: right;
          padding: 0 0 10px 10px;
        }
        .choose-your-options__image-container {
          padding: 20px 0px 20px 0px;
        }
        .choose-your-options__image {
          width: 100%;
        }
        .choose-your-options__inputs {
          padding: 0px 0px 12px 0px;
        }

        :global(.deal-product-selector__empty-message) {
          margin: 0 auto 20px;
          padding: 6px 12px;
          color: ${theme.commoncolors.alert};
          border: 4px solid ${theme.commoncolors.alert};
          border-radius: 4px;
          text-align: center;
        }
        :global(.deal-product-selector__empty-message.hidden) {
          display: none;
        }
        :global(.deal-product-selector__footnote) {
          margin: -8px 0 10px 0;
        }
        :global(.choose-your-options .modal-body) {
          padding: ${isExpressDealPage ? '0 5px 20px 5px' : '0 20px 20px 20px'};
        }
        :global(.choose-your-options__discount .deal-main-deal__discount) {
          background: ${theme.colors.primary};
        }
        :global(.choose-your-options .modal-content) {
          border: 1px solid ${theme.colors.primary};
        }
        :global(.size-larger) {
          position: ${layoutOptions.travel ? 'static' : 'sticky'};
          bottom: 0px;
          z-index: 1001;
        }
      `}</style>
    </>
  );
};

DealChooseYourOptionsModal.propTypes = {
  buttonRef: PropTypes.object,
  deal: PropTypes.object,
  finePrintCollapsed: PropTypes.bool,
  handleBuyButtonClick: PropTypes.func,
  initialColumnSel: PropTypes.number,
  isOptionSelected: PropTypes.func,
  isVisible: PropTypes.bool,
  layout: PropTypes.string,
  secondCheckout: PropTypes.bool,
  setFinePrintCollapsed: PropTypes.func,
  setIsCYOSelected: PropTypes.func,
};

export default DealChooseYourOptionsModal;
